// src/features/auth/components/SignUpForm.tsx

import React, { useState, useMemo } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Typography,
  Autocomplete,
} from "@mui/material";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import { SignUpData } from "../types";
import { authService } from "../services/authService";
import { COUNTRY_CODES } from "../constants/countryCodes";
import { Country, State, City, ICountry, IState, ICity } from "country-state-city";

interface SignUpFormProps {
  onSubmit: (data: SignUpData) => void;
  isLoading: boolean;
  error: string | null;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<SignUpData>({
    prefix: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneCode: "+66",
    phoneNumber: "",
    country: "",
    address: "",
    province: "",
    district: "",
    postcode: "",
    password: "",
    confirmPassword: "",
  });

  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    newsletter: false,
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [cfToken, setCfToken] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const states = useMemo(() => {
    if (!formData.country) return [];
    return State.getStatesOfCountry(formData.country);
  }, [formData.country]);

  const cities = useMemo(() => {
    if (!formData.country || !formData.province) return [];
    return City.getCitiesOfState(formData.country, formData.province);
  }, [formData.country, formData.province]);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push("At least 8 characters");
    if (!/[A-Z]/.test(password)) errors.push("At least 1 uppercase letter");
    if (!/[0-9]/.test(password)) errors.push("At least 1 number");
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password))
      errors.push("At least 1 special character");
    return errors;
  };

  const handleChange =
    (field: keyof SignUpData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (field === "phoneNumber") {
        value = value.replace(/[^0-9]/g, "");
      }
      setFormData({ ...formData, [field]: value });
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const handleSelectChange = (field: keyof SignUpData) => (e: any) => {
    const value = e.target.value as string;
    if (field === "country") {
      setFormData({ ...formData, country: value, province: "", district: "", postcode: "" });
    } else if (field === "province") {
      setFormData({ ...formData, province: value, district: "", postcode: "" });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const validateStep1 = (): boolean => {
    const errors: Record<string, string> = {};

    if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    const pwErrors = validatePassword(formData.password);
    if (pwErrors.length > 0) {
      errors.password = pwErrors.join(", ");
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "Please enter your first name";
    }
    if (!formData.lastName.trim()) {
      errors.lastName = "Please enter your last name";
    }
    if (formData.phoneNumber.length < 7) {
      errors.phoneNumber = "Phone number must be at least 7 digits";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (step === 2 && !validateStep2()) return;
    if (step !== 1) { setStep(step + 1); return; }

    if (!validateStep1()) return;

    setCheckingEmail(true);
    try {
      const result = await authService.checkEmail(formData.email);
      if (!result.available) {
        setFieldErrors((prev) => ({ ...prev, email: result.message }));
        return;
      }
    } catch {
      setFieldErrors((prev) => ({ ...prev, email: 'Unable to verify email. Please try again.' }));
      return;
    } finally {
      setCheckingEmail(false);
    }

    setStep(2);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  // Step 1: Personal Information
  const renderStep1 = () => (
    <Box>
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleChange("email")}
        error={!!fieldErrors.email}
        helperText={fieldErrors.email}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? "text" : "password"}
        value={formData.password}
        onChange={handleChange("password")}
        error={!!fieldErrors.password}
        helperText={fieldErrors.password}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Confirm Password"
        type={showConfirmPassword ? "text" : "password"}
        value={formData.confirmPassword}
        onChange={handleChange("confirmPassword")}
        error={!!fieldErrors.confirmPassword}
        helperText={fieldErrors.confirmPassword}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreements.terms}
              onChange={(e) =>
                setAgreements({ ...agreements, terms: e.target.checked })
              }
            />
          }
          label={
            <Typography variant="body2">
              By submitting this form, I confirm that I have read and agree to
              the the Terms & Conditions and Privacy Policy  of Sirivannavari
              Brand.
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={agreements.newsletter}
              onChange={(e) =>
                setAgreements({ ...agreements, newsletter: e.target.checked })
              }
            />
          }
          label={
            <Typography variant="body2">Subscribe to newsletter</Typography>
          }
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <ReCAPTCHA
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
          onChange={(token) => setCfToken(token)}
          onExpired={() => setCfToken(null)}
          onErrored={() => setCfToken(null)}
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleNext}
        disabled={
          !formData.email ||
          !formData.password ||
          !formData.confirmPassword ||
          !agreements.terms ||
          !cfToken ||
          checkingEmail
        }
        sx={{
          bgcolor: "#000",
          "&:hover": { bgcolor: "#333" },
          textTransform: "none",
          py: 1.5,
        }}
      >
        {checkingEmail ? 'Checking...' : 'Next'}
      </Button>
    </Box>
  );

  // Step 2: Contact Information
  const renderStep2 = () => (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        align="left"
        sx={{ mb: 3 }}
      >
        Personal Information
        <Typography component="span" color="error">
          *
        </Typography>
      </Typography>

      <TextField
        fullWidth
        label="First name"
        value={formData.firstName}
        onChange={handleChange("firstName")}
        error={!!fieldErrors.firstName}
        helperText={fieldErrors.firstName}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Last name"
        value={formData.lastName}
        onChange={handleChange("lastName")}
        error={!!fieldErrors.lastName}
        helperText={fieldErrors.lastName}
        sx={{ mb: 3 }}
      />
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Code</InputLabel>
          <Select
            value={formData.phoneCode}
            label="Code"
            onChange={handleSelectChange("phoneCode")}
          >
            {COUNTRY_CODES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phoneNumber}
          onChange={handleChange("phoneNumber")}
          error={!!fieldErrors.phoneNumber}
          helperText={fieldErrors.phoneNumber}
          inputProps={{ maxLength: 12 }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Button
          fullWidth
          variant="contained"
          onClick={handleNext}
          disabled={
            !formData.firstName || !formData.lastName || !formData.phoneNumber
          }
          sx={{
            bgcolor: "#000",
            "&:hover": { bgcolor: "#333" },
            textTransform: "none",
            py: 1.5,
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  // Step 3: Address
  const renderStep3 = () => (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        align="left"
        sx={{ mb: 3 }}
      >
        Address (May be filled in later)
      </Typography>

      {/* 1. Country */}
      <Autocomplete
        options={allCountries}
        getOptionLabel={(option: ICountry) => option.name}
        value={allCountries.find((c) => c.isoCode === formData.country) || null}
        onChange={(_e, newValue) => {
          const value = newValue ? newValue.isoCode : "";
          setFormData({ ...formData, country: value, province: "", district: "", postcode: "" });
        }}
        renderInput={(params) => <TextField {...params} label="Country" />}
        ListboxProps={{ style: { maxHeight: 300 } }}
        sx={{ mb: 2 }}
      />

      {/* 2. Address */}
      <TextField
        fullWidth
        label="Address"
        value={formData.address}
        onChange={handleChange("address")}
        sx={{ mb: 2 }}
      />

      {/* 3. Province / State */}
      <Autocomplete
        options={states}
        getOptionLabel={(option: IState) => option.name}
        value={states.find((s) => s.isoCode === formData.province) || null}
        onChange={(_e, newValue) => {
          const value = newValue ? newValue.isoCode : "";
          setFormData({ ...formData, province: value, district: "" });
        }}
        disabled={!formData.country}
        renderInput={(params) => <TextField {...params} label="Province / State" />}
        slotProps={{ listbox: { style: { maxHeight: 300 } } }}
        sx={{ mb: 2 }}
      />

      {/* 4. District / City */}
      <Autocomplete
        options={cities}
        getOptionLabel={(option: ICity) => option.name}
        value={cities.find((c) => c.name === formData.district) || null}
        onChange={(_e, newValue) => {
          const value = newValue ? newValue.name : "";
          setFormData({ ...formData, district: value });
        }}
        disabled={!formData.province}
        renderInput={(params) => <TextField {...params} label="District / City" />}
        slotProps={{ listbox: { style: { maxHeight: 300 } } }}
        sx={{ mb: 2 }}
      />

      {/* 5. Postcode */}
      <TextField
        fullWidth
        label="Postcode"
        value={formData.postcode}
        onChange={handleChange("postcode")}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: "flex", gap: 1 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Button
          fullWidth
          variant="contained"
          onClick={() => onSubmit({ ...formData, cfToken: cfToken ?? undefined })}
          disabled={isLoading || !agreements.terms}
          sx={{
            bgcolor: "#000",
            "&:hover": { bgcolor: "#333" },
            textTransform: "none",
            py: 1.5,
          }}
        >
          {isLoading ? "Creating Account..." : "Submit"}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </Box>
  );
};
